import { API_URL } from '~/constants';
import { ROUTE as ROUTE_APP } from '~/app/routes';
import { ROUTE } from '~/public/routes';
import { useModalStore } from '~/stores/Modal';
import { useSessionStore } from '~/stores/Session';
import { convertValuesToString, keysToCamelCase, keysToSnakeCase } from '~/util';
import { getQueryString } from '~/hooks/useQueryParams';

interface Options {
  query?: Record<string, any>;
  platformId?: string;
  sandbox?: boolean;
  idempotency?: string;
  reCaptchaToken?: string;
  hideModeHeader?: boolean;
  allowNumbersInRequest?: boolean;
}

class Client {
  async get<Request, Response>(path: string, options?: Options): Promise<Response> {
    return this.request<Request, Response>(path, 'GET', undefined, options);
  }

  async delete<Request, Response>(path: string, options?: Options): Promise<Response> {
    return this.request<Request, Response>(path, 'DELETE', undefined, options);
  }

  async patch<Request, Response>(path: string, requestBody: Request, options?: Options): Promise<Response> {
    return this.request<Request, Response>(path, 'PATCH', requestBody, options);
  }

  async post<Request, Response>(path: string, requestBody: Request, options?: Options): Promise<Response> {
    return this.request<Request, Response>(path, 'POST', requestBody, options);
  }

  async request<Request, Response>(
    path: string,
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    requestBody?: Request,
    options?: Options
  ): Promise<Response> {
    let query = '';

    if (options?.query) {
      query = getQueryString(options.query);
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (options?.idempotency) {
      headers['Idempotency-Key'] = options.idempotency;
    }

    if (options?.platformId || useSessionStore.getState().currentUser?.defaultPlatformId) {
      headers['Platform-Id'] = options?.platformId ?? useSessionStore.getState().currentUser?.defaultPlatformId ?? '';
    }

    if (!options?.hideModeHeader) {
      headers['Live-Mode'] = options?.sandbox ?? useSessionStore.getState().isSandbox ? 'No' : 'Yes';
    }

    if (options?.reCaptchaToken && options?.reCaptchaToken?.length > 0) {
      headers['g-recaptcha-response'] = options.reCaptchaToken;
    }

    const request = new Request(API_URL + path + query, {
      method,
      headers,
      body: requestBody
        ? options?.allowNumbersInRequest
          ? JSON.stringify(keysToSnakeCase(requestBody))
          : JSON.stringify(keysToSnakeCase(convertValuesToString(requestBody))) ?? {}
        : undefined,
      credentials: 'include',
    });

    return fetch(request)
      .then((response) => {
        if (response.status === 401) {
          if (useSessionStore.getState().isSignedIn() || useSessionStore.getState().authRequired()) {
            this.logout();
            return Promise.reject();
          }
          throw response;
        }
        return response;
      })
      .then(async (response) => {
        try {
          const json = await response.json();

          if (response.ok) {
            return Promise.resolve(json);
          }

          return Promise.reject(json);
        } catch (message) {
          return Promise.reject(
            JSON.stringify({
              message,
            })
          );
        }
      })
      .then((response) => keysToCamelCase(response))
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  private logout() {
    const email = useSessionStore.getState().currentUser?.email;

    if (!useSessionStore.getState().authRequired()) {
      useSessionStore.getState().clearCookies();
    }

    if (email) {
      if (useModalStore.getState().getModalIsOpen('Authenticate')) {
        return;
      }
      useModalStore.getState().openModal('PasswordPrompt');
    } else {
      useModalStore.getState().closeModal();
      if (window.location.pathname.startsWith(ROUTE_APP.ROOT)) {
        window.location.replace(useSessionStore.getState().authRequired() ? ROUTE.AUTH : ROUTE.LOGIN);
      }
    }
  }
}

export const client = new Client();
